import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Loader from "react-loader-spinner";
import { useIntercom } from "react-use-intercom";
import SideBar from "../../Sections/SideBar";
import Navbar from "../../Sections/NavBar";
import MobileNav from "../../Sections/MobileNav";

import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../app-store/userSlice";
import InitiateTransfer from "./InitiateTransfer";
import ConfirmTransfer from "./ConfirmTransfer";
import Status from "./Status";
import httpClient from "../../../custom-hooks/httpClient";
import TwoFactorAuth from "../../2FA-OTP";

function TransferWalletToBank() {
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const userId = localStorage.getItem("id");

  const user = useSelector((state) => state.user.user);

  const dispatch = useDispatch();

  const { boot } = useIntercom();

  const [stage, setStage] = useState("initiate"); // initiate, confirm, status
  const [transferDetails, setTransferDetails] = useState({
    transaction_pin: "",
    account_number: "",
    account_name: "",
    bank_code: "",
    amount: "",
    wallet_account_number: "",
    narration: "",
    wallet_name: "",
  });

  const [bankName, setBankName] = useState("");
  const [transferStatus, setTransferStatus] = useState("");
  const [transferReference, setTransferReference] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [bankAccountName, setBankAccountName] = useState("");
  const [accountDetails, setAccountDetails] = useState({});
  const [notificationChannel, setNotificationChannel] = useState("");
  const [recipient, setRecipient] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChangeTransferDetails = (e) => {
    if (e.target.name === "bank") {
      setTransferDetails({
        ...transferDetails,
        bank_code: e.target.value.split("***")[0],
      });
      setBankName(e.target.value.split("***")[1]);
    } else if (e.target.name === "account_number") {
      setBankAccountNumber(e.target.value);
      setTransferDetails({
        ...transferDetails,
        [e.target.name]: e.target.value,
      });
    } else if (e.target.name === "wallet_account_number") {
      const selectedWallet = user.wallets.find(
        (wallet) => wallet.account_no === e.target.value
      );
      if (selectedWallet) {
        setTransferDetails({
          ...transferDetails,
          wallet_account_number: selectedWallet.account_no,
          wallet_name: selectedWallet.name,
        });
      }
    } else {
      setTransferDetails({
        ...transferDetails,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleInitiateTransfer = (e) => {
    e.preventDefault();
    setStage("confirm");
  };

  const onConfirm = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const {
      beneficiaryAccountNumber,
      beneficiaryNameEnquiryRefrenceNumber,
      bankVerificationNumber,
      beneficiaryAccountName,
      beneficiaryBankCode,
      kyclevel,
    } = accountDetails;

    // Set transfer amount to number
    const transfer = {
      ...transferDetails,
      beneficiaryAccountNumber,
      beneficiaryNameEnquiryRefrenceNumber,
      beneficiaryBankVerificationNumber: bankVerificationNumber,
      beneficiaryAccountName,
      beneficiaryBankCode,
      beneficiaryKYCLevel: kyclevel,
      channelCode: "1",
      amount: Number(transferDetails.amount),
    };

    try {
      const response = await httpClient.post(
        `/transfers/wallet-to-bank/transfer-funds`,
        {
          ...transfer,
        }
      );

      const { status, data } = response;

      if (status === 202) {
        setNotificationChannel(data.notificationChannel);
        setRecipient(data.recipient);
        setStage("authenticate");
      } else if (status === 200) {
        setTransferReference(data.paymentReference);
        setTransferStatus("successful");
        setStage("status");
      }
    } catch (err) {
      setIsProcessing(false);
      console.error(err);
      toast.error(err.response?.data.message);
      setTransferStatus("failed");
      setStage("status");
    }
  };

  const handleConfirmTransfer = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    const {
      beneficiaryAccountNumber,
      beneficiaryNameEnquiryRefrenceNumber,
      bankVerificationNumber,
      beneficiaryAccountName,
      beneficiaryBankCode,
      kyclevel,
    } = accountDetails;

    // Set transfer amount to number
    const transfer = {
      ...transferDetails,
      beneficiaryAccountNumber,
      beneficiaryNameEnquiryRefrenceNumber,
      beneficiaryBankVerificationNumber: bankVerificationNumber,
      beneficiaryAccountName,
      beneficiaryBankCode,
      beneficiaryKYCLevel: kyclevel,
      channelCode: "1",
      amount: Number(transferDetails.amount),
    };
    const otp = document.getElementById("otp").value.trim(); // Get OTP from user input.

    try {
      const { status, data } = await httpClient.post(
        `/transfers/wallet-to-bank/transfer-funds`,
        {
          ...transfer,
          otp,
        }
      );

      if (status === 200) {
        setTransferReference(data.paymentReference);
        setTransferStatus("successful");
        setStage("status");
      }
    } catch (err) {
      // Check for 401 status with "invalid otp" message.
      if (
        err.response?.status === 401 &&
        err.response?.data?.message === "Invalid otp"
      ) {
        setErrorMessage("Invalid OTP! Please, check and try again.");
        document.getElementById("otp").value = ""; // Reset OTP input field.
        setIsProcessing(false);
        setTimeout(() => {
          setErrorMessage("");
        }, 5000);
      } else {
        const errorMessage =
          err.response?.data.message || "Transaction Failed, Try Again!";
        toast.error(errorMessage);
        setTransferStatus("failed");
        setStage("status");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveBeneficiary = async () => {
    const newValue = !saveBeneficiary;
    setSaveBeneficiary(newValue);

    if (!newValue) return;

    try {
      if (!transferDetails.bank_code) {
        toast.error(
          "Receiving bank is required to save a beneficiary. Please, select a bank.",
        );
        setSaveBeneficiary(false);
        return;
      }
      const beneficiaryData = {
        user_id: user.id,
        name: transferDetails.account_name,
        recipient_account_number: bankAccountNumber,
        account_number: user.wallets[0].account_no,
        account_type: transferDetails.account_type || "",
        bank_code: transferDetails.bank_code,
        bank_image: transferDetails.bank_image || "",
      };

      const { status } = await httpClient.post(
        `/beneficiaries/${bankAccountNumber}`,
        beneficiaryData,
      );

      if (status === 200) {
        toast.success("Beneficiary Saved Successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data.message ||
          "Error Saving Beneficiary! Please, check that the beneficiary does not already exist.",
      );
    }
  };

  useEffect(() => {
    httpClient
      .get(`/users/${userId}`)
      .then(({ status, data }) => {
        if (status === 200) {
          // console.log('Data: ', user);
          if (data.user) dispatch(setUser(data.user));
          boot({
            name: `${data.user.first_name} ${data.user.last_name}`,
          });
        }
      })
      .catch((err) => {
        // console.log('error fetching user: ', err);
        // toast.error("Error getting user");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return (
      <div
        className="text-center align-items-center"
        style={{ marginTop: "25%" }}
      >
        <Loader type="Oval" color="#1c743a" />
      </div>
    );
  }

  return (
    <>
      <Navbar dashboard />
      <MobileNav />
      <section>
        <div className="container-fluid">
          <div className="row">
            <SideBar />
            <div className="col-12 col-lg-6 col-xl-8 offset-lg-3 offset-xl-2 py-7 py-lg-9 px-lg-7">
              {stage === "initiate" && (
                <InitiateTransfer
                  user={user}
                  handleInitiateTransfer={handleInitiateTransfer}
                  handleChangeTransferDetails={handleChangeTransferDetails}
                  transferDetails={transferDetails}
                  bankName={bankName}
                  setBankName={setBankName}
                  setTransferDetails={setTransferDetails}
                  bankAccountNumber={bankAccountNumber}
                  setBankAccountNumber={setBankAccountNumber}
                  bankAccountName={bankAccountName}
                  setBankAccountName={setBankAccountName}
                  accountDetails={accountDetails}
                  setAccountDetails={setAccountDetails}
                  saveBeneficiary={saveBeneficiary}
                  handleSaveBeneficiary={handleSaveBeneficiary}
                />
              )}
              {stage === "confirm" && (
                <ConfirmTransfer
                  handleChangeTransferDetails={handleChangeTransferDetails}
                  transferDetails={transferDetails}
                  bankName={bankName}
                  isProcessing={isProcessing}
                  onConfirm={onConfirm}
                />
              )}
              {stage === "authenticate" && (
                <TwoFactorAuth
                  handleConfirmTransfer={handleConfirmTransfer}
                  notificationChannel={notificationChannel}
                  recipient={recipient}
                  errorMessage={errorMessage}
                />
              )}
              {stage === "status" && (
                <Status
                  transferDetails={transferDetails}
                  transferStatus={transferStatus}
                  transferReference={transferReference}
                  bankName={bankName}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default TransferWalletToBank;
